/**
 * Export Utilities
 * Helper functions for export operations
 */

import { ExportFormat, ExportConfig, ExportMetadata } from './exportTypes';

/**
 * Generate filename with timestamp
 */
export const generateFilename = (
    baseName: string,
    format: ExportFormat,
    includeTimestamp: boolean = true
): string => {
    const timestamp = includeTimestamp
        ? `-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}`
        : '';
    const extension = format === 'markdown' ? 'md' : format;
    return `${baseName}${timestamp}.${extension}`;
};

/**
 * Get file extension for format
 */
export const getFileExtension = (format: ExportFormat): string => {
    return format === 'markdown' ? 'md' : format;
};

/**
 * Get MIME type for format
 */
export const getMimeType = (format: ExportFormat): string => {
    const mimeTypes: Record<ExportFormat, string> = {
        png: 'image/png',
        jpeg: 'image/jpeg',
        svg: 'image/svg+xml',
        gif: 'image/gif',
        json: 'application/json',
        markdown: 'text/markdown',
    };
    return mimeTypes[format];
};

/**
 * Download blob as file
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Download string as file
 */
export const downloadString = (content: string, filename: string, mimeType: string): void => {
    const blob = new Blob([content], { type: mimeType });
    downloadBlob(blob, filename);
};

/**
 * Create canvas from HTML element
 */
export const createCanvasFromElement = async (
    element: HTMLElement,
    scale: number = 1,
    backgroundColor?: string
): Promise<HTMLCanvasElement> => {
    // We'll use html2canvas for this
    // For now, create a basic implementation
    const canvas = document.createElement('canvas');
    const rect = element.getBoundingClientRect();

    canvas.width = rect.width * scale;
    canvas.height = rect.height * scale;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Could not get canvas context');
    }

    // Fill background if specified
    if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Scale context
    ctx.scale(scale, scale);

    return canvas;
};

/**
 * Convert canvas to blob
 */
export const canvasToBlob = (
    canvas: HTMLCanvasElement,
    format: 'png' | 'jpeg',
    quality: number = 1
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        // Validate canvas
        if (!canvas) {
            reject(new Error('Canvas is null or undefined'));
            return;
        }

        if (canvas.width === 0 || canvas.height === 0) {
            reject(new Error(`Canvas has zero dimensions: ${canvas.width}x${canvas.height}`));
            return;
        }

        // Check if canvas has content
        try {
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Could not get canvas 2d context'));
                return;
            }

            // Sample a small area to check for content
            const sampleSize = Math.min(10, canvas.width, canvas.height);
            const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
            const hasContent = imageData.data.some(value => value !== 0);

            if (!hasContent) {
                console.warn('⚠️ Warning: Canvas appears to be empty (all pixels are transparent/black)');
            }
        } catch (error) {
            console.warn('Could not validate canvas content:', error);
        }

        // Convert to blob
        const mimeType = `image/${format}`;

        try {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        console.error('❌ canvas.toBlob returned null');
                        reject(new Error('Failed to create blob from canvas - toBlob returned null. This may be due to tainted canvas (CORS issue) or browser limitations.'));
                    }
                },
                mimeType,
                quality
            );
        } catch (error: any) {
            console.error('❌ Exception during toBlob:', error);
            reject(new Error(`Exception during blob creation: ${error.message}`));
        }
    });
};
/**
 * Get element bounds including all children
 */
export const getElementBounds = (element: HTMLElement): DOMRect => {
    return element.getBoundingClientRect();
};

/**
 * Create export metadata
 */
export const createExportMetadata = (
    config: ExportConfig,
    width: number,
    height: number,
    diagramName?: string
): ExportMetadata => {
    return {
        width,
        height,
        format: config.format,
        quality: config.quality,
        timestamp: Date.now(),
        diagramName,
        version: '1.0.0',
    };
};

/**
 * Validate export configuration
 */
export const validateExportConfig = (config: Partial<ExportConfig>): boolean => {
    if (!config.format) return false;

    const validFormats: ExportFormat[] = ['png', 'jpeg', 'svg', 'gif', 'json', 'markdown'];
    if (!validFormats.includes(config.format)) return false;

    if (config.quality && ![1, 2, 4].includes(config.quality)) return false;

    if (config.format === 'gif') {
        if (config.gifDurationSeconds !== undefined &&
            (config.gifDurationSeconds < 0.5 || config.gifDurationSeconds > 10)) return false;
        if (config.gifFps !== undefined &&
            (!Number.isInteger(config.gifFps) || config.gifFps < 5 || config.gifFps > 24)) return false;
        if (config.gifMaxColors !== undefined &&
            (!Number.isInteger(config.gifMaxColors) || config.gifMaxColors < 32 || config.gifMaxColors > 256)) return false;
    }

    return true;
};

/**
 * Create default export config
 */
export const createDefaultConfig = (format: ExportFormat): ExportConfig => {
    return {
        format,
        quality: 2,
        transparent: format !== 'jpeg',
        includeGrid: false,
        backgroundColor: '#ffffff',
        filename: `flowi-diagram`,
        exportFullDiagram: true,
        gifDurationSeconds: 3,
        gifFps: 12,
        gifLoop: true,
        gifMaxColors: 256,
    };
};

/**
 * Sanitize filename
 */
export const sanitizeFilename = (filename: string): string => {
    return filename
        .replace(/[^a-z0-9_-]/gi, '_')
        .replace(/_{2,}/g, '_')
        .toLowerCase();
};

/**
 * Calculate export dimensions
 */
export const calculateExportDimensions = (
    element: HTMLElement,
    quality: number
): { width: number; height: number } => {
    const rect = element.getBoundingClientRect();
    return {
        width: Math.floor(rect.width * quality),
        height: Math.floor(rect.height * quality),
    };
};
